import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, ListGroup, Form, Button, Card } from 'react-bootstrap';
import { FaTrash, FaPlus, FaMinus } from 'react-icons/fa';
import Message from '../components/Message';
import Loader from '../components/Loader';
import { addToCart, removeFromCart, clearCartItems } from '../slices/cartSlice';
import { useCreatePurchaseMutation } from '../slices/purchasesApiSlice';
import { useGetSuppliersQuery } from '../slices/suppliersApiSlice';
import { toast } from 'react-toastify';

const PurchasesForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const cart = useSelector((state) => state.cart);
  const { cartItems } = cart;

  const [supplierSearch, setSupplierSearch] = useState('');
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [note, setNote] = useState('');
  const [purchaseDate, setPurchaseDate] = useState(new Date().toISOString().split('T')[0]);
  const [expiryDate, setExpiryDate] = useState(new Date().toISOString().split('T')[0]);

  const [totalCosts, setTotalCosts] = useState({});
  const [unitCosts, setUnitCosts] = useState({});
  const [profits, setProfits] = useState({});

  const { data: suppliersData, isLoading, error } = useGetSuppliersQuery({ keyword: supplierSearch });
  const [createPurchase, { isLoading: savingPurchases, error: purchasesError }] = useCreatePurchaseMutation();

  // Filter suppliers based on search input
  useEffect(() => {
    if (suppliersData?.suppliers) {
      const filtered = suppliersData.suppliers.filter((supplier) =>
        supplier.name.toLowerCase().includes(supplierSearch.toLowerCase())
      );
      setFilteredSuppliers(filtered);
    }
  }, [supplierSearch, suppliersData]);

  // Dynamically update totalCost, unitCost, and profit
  useEffect(() => {
    const updatedUnitCosts = {};
    const updatedProfits = {};
    cartItems.forEach((item) => {
      const totalCost = Math.round(totalCosts[item._id] || 0);
      //const totalCost = (totalCosts[item._id]).toFixed(2) || 0;
      const unitCost = totalCost > 0 ? (totalCost / item.qty).toFixed(2) : 0;
      const profit = ((item.qty * item.price) - totalCost).toFixed(2);
      updatedUnitCosts[item._id] = (unitCost).toFixed(2);
      updatedProfits[item._id] = profit;
    });
    setUnitCosts(updatedUnitCosts);
    setProfits(updatedProfits);
  }, [totalCosts, cartItems]);

  const handleCostChange = (id, change) => {
    setTotalCosts((prevState) => ({
      ...prevState,
      [id]: (prevState[id] || 0) + change,
    }));
  };

  const handleSupplierSelect = (supplier) => {
    setSelectedSupplier(supplier);
    setSupplierSearch(supplier.name);
    setFilteredSuppliers([]);
  };

  const placePurchaseHandler = async () => {
    try {
      if (!selectedSupplier) {
        toast.error('Please select a supplier');
        return;
      }

      if (cartItems.length === 0) {
        toast.error('Your cart is empty');
        return;
      }

      const purchaseData = {
        purchaseItems: cartItems.map((item) => ({
          name: item.name,
          qty: item.qty,
          price: item.price,
          image: item.images?.[0],
          
          product: item._id,
        })),
        totalCost: Math.round(totalCosts),
         
        supplierDetails: selectedSupplier._id,
        note,
        date: purchaseDate,
      };

      const response = await createPurchase(purchaseData).unwrap();
      console.log('Response:', response); // Debugging backend response
      dispatch(clearCartItems());
      toast.success('Purchase successfully recorded');
      navigate('/house/HomeScreen');
    } catch (err) {
      console.error('Error:', err); // Log error object for debugging
      toast.error(err?.data?.message || 'Failed to record purchase');
    }
  };

  return (
    <Row>
      <Col md={8}>
        <h1>Purchases Form</h1>
        {cartItems.length === 0 ? (
          <Message>
            Your cart is empty. <Button onClick={() => navigate('/house/HomeScreen')}>Go Back</Button>
          </Message>
        ) : (
          <ListGroup variant="flush">
            {cartItems.map((item) => (
              <ListGroup.Item key={item._id}>
                <Row>
                  <Col md={2}>
                    {item.images?.[0] && (
                      <img
                        src={item.images[0]}
                        alt={item.name}
                        style={{
                          width: '50px',
                          height: '50px',
                          objectFit: 'cover',
                        }}
                      />
                    )}
                  </Col>
                  <Col>{item.name}</Col>
                  <Col md={2}>${item.price}</Col>
                  <Col md={2}>
                    <Form.Control
                      as="select"
                      value={item.qty}
                      onChange={(e) => dispatch(addToCart({ ...item, qty: Number(e.target.value) }))}
                    >
                      {[...Array(item.countInStock).keys()].map((x) => (
                        <option key={x + 1} value={x + 1}>
                          {x + 1}
                        </option>
                      ))}
                    </Form.Control>
                  </Col>
                  <Col md={2}>${(item.qty * item.price).toFixed(2)}</Col>
                  <Col md={1}>
                    <Button variant="light" onClick={() => dispatch(removeFromCart(item._id))}>
                      <FaTrash />
                    </Button>
                  </Col>
                </Row>
                <div className="mt-3">

                  <Form.Group controlId="totalCost">
  <Form.Label>Total Cost</Form.Label>
  <div className="d-flex align-items-center">
    <Button
      variant="light"
      onClick={() => handleCostChange(item._id, -10)}
      disabled={(totalCosts[item._id] || 0) <= 0}
    >
      <FaMinus />
    </Button>
    <span className="mx-2">{totalCosts[item._id] || 0}</span>
    <Button variant="light" onClick={() => handleCostChange(item._id, 10)}>
      <FaPlus />
    </Button>
  </div>
</Form.Group>
                  


                  <Form.Group controlId={`unitCost-${item._id}`} className="mt-2">
                    <Form.Label>Unit Cost</Form.Label>
                    <Form.Control type="text" value={unitCosts[item._id] || 0} readOnly />
                  </Form.Group>
                  <Form.Group controlId={`profit-${item._id}`} className="mt-2">
                    <Form.Label>Profit</Form.Label>
                    <Form.Control type="text" value={profits[item._id] || 0} readOnly />
                  </Form.Group>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Col>
      <Col md={4}>
        <Card>
          <ListGroup variant="flush">
            <ListGroup.Item>
              <h2>Select Supplier</h2>
              {isLoading ? (
                <Loader />
              ) : error ? (
                <Message variant="danger">{error?.data?.message || 'Failed to load suppliers'}</Message>
              ) : (
                <>
                  <Form.Group controlId="supplierSearch">
                    <Form.Label>Search Supplier</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Type supplier name"
                      value={supplierSearch}
                      onChange={(e) => setSupplierSearch(e.target.value)}
                    />
                  </Form.Group>
                  {filteredSuppliers.length > 0 && (
                    <ListGroup className="dropdown-menu mt-2">
                      {filteredSuppliers.map((supplier) => (
                        <ListGroup.Item
                          key={supplier._id}
                          action
                          onClick={() => handleSupplierSelect(supplier)}
                        >
                          {supplier.name} ({supplier.company})
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}
                  {selectedSupplier && (
                    <div className="mt-3">
                      <strong>Selected Supplier:</strong>
                      <p>
                        {selectedSupplier.name} ({selectedSupplier.company})<br />
                        Phone: {selectedSupplier.telephone}<br />
                        Address: {selectedSupplier.address}<br />
                        SERIAL: {selectedSupplier.serial}
                      </p>
                      <Form.Group controlId="note">
                        <Form.Label>Note</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter additional notes"
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                        />
                      </Form.Group>
                      <Form.Group controlId="purchaseDate" className="mt-2">
                        <Form.Label>Purchase Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={purchaseDate}
                          onChange={(e) => setPurchaseDate(e.target.value)}
                        />
                      </Form.Group>
                      <Form.Group controlId="expiryDate" className="mt-2">
                        <Form.Label>Expiry Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={expiryDate}
                          onChange={(e) => setExpiryDate(e.target.value)}
                        />
                      </Form.Group>
                    </div>
                  )}
                </>
              )}
            </ListGroup.Item>
            {purchasesError && (
              <ListGroup.Item>
                <Message variant="danger">{purchasesError.message}</Message>
              </ListGroup.Item>
            )}
            <ListGroup.Item>
              <Button
                type="button"
                className="btn-block"
                disabled={cartItems.length === 0 || !selectedSupplier}
                onClick={placePurchaseHandler}
              >
                Submit Purchase
              </Button>
              {savingPurchases && <Loader />}
            </ListGroup.Item>
          </ListGroup>
        </Card>
      </Col>
    </Row>
  );
};
export default PurchasesForm;